import * as THREE from "three";
import { HqMaterials } from "./hqMaterials";
import { lib } from "./views/common";
import { reducedVfx } from "../ui/settings";
import { burstPlan, climbPath } from "../ui/revealPlan";
import { groundShadow, stageLights } from "./chestLook";
import { hingeAngle } from "./chestShow";
import { ChestCamera } from "./chestCamera";
import { ChestModel } from "./chestModel";
import { StageFinale } from "./chestFinale";
import { ChestFlight, type Framing } from "./chestFlight";
import { StageCanvas } from "./stageCanvas";
import { StageClock } from "./stageClock";
import { StageRays } from "./stageRays";

const RARITIES = ["common", "uncommon", "rare", "epic", "legendary", "mythic"] as const;

/**
 * The reward chest (art/hq_chest.py, v8) in its own transparent WebGL canvas: the lighting rig from art/SHADING.md, its
 * own bloom, the chest (chestModel.ts) and its show (chestShow.ts). While the player decides it breathes (`idle`) and
 * now and then rattles; `enter` drops it in, or `flyIn` lifts it off the end card. With `climb` it starts as a common
 * chest and each `upgrade` swaps it for the next tier on revealPlan.climbPath's way to its real rarity — the roll was
 * made before; only its showing climbs. `open` plays `open` (the burst lands as the lid is thrown, in slow motion), then
 * loops `opened` while the camera turns slowly round it; the legendary and the mythic get their finales. `onOpened`
 * fires when `open` ends, and only then. Presentation only: the chest is banked by the caller.
 */
export class ChestStage {
  private readonly gl: StageCanvas;
  private readonly scene = new THREE.Scene();
  private readonly view: ChestCamera;
  private get camera(): THREE.PerspectiveCamera { return this.view.camera; }
  private get vfx() { return this.gl.vfx; }
  get canvas(): HTMLCanvasElement { return this.gl.canvas; }
  private model: ChestModel;
  private phase: "shake" | "open" | "done" = "shake";
  private openTime = 0;
  private burstDone = false;
  private fxClock = 0;
  private beckon = 0;
  private onOpened: (() => void) | null = null;
  /** The target rarity's bounds at rest (stage world space): the framing, which never moves while the chest climbs. */
  private readonly bounds: THREE.Box3;
  /** The ranks the chest climbs through (revealPlan.climbPath; just the target without `climb`) and where it is on it. */
  readonly path: readonly number[];
  private step = 0;
  private readonly clock = new StageClock();
  private readonly finale: StageFinale;
  /** The framing now (CSS px of the canvas): where the chest sits and the share of the height it fills. */
  private framing = { x: 0, y: 0, fill: 0.6, w: 1, h: 1 };
  private flight: ChestFlight | null = null;
  static readonly ShakeEvery = 0.6;
  static readonly AuraEvery = 0.4;
  /** While it waits, the chest rattles on its own this often (s): it wants to be opened. */
  static readonly BeckonEvery = 2.6;
  static readonly Scale = ChestModel.Scale;

  static pixelRatio(): number { return StageCanvas.pixelRatio(); }

  /** The models a reveal of `rarity` needs (its climb's tiers with `climb`). */
  static modelsFor(rarity: string, climb = true): string[] {
    const r = ChestModel.rankOf(rarity);
    return (climb ? climbPath(r) : [r]).map((k) => `chest_${RARITIES[k]}`);
  }

  /** True when every model the reveal needs is loaded — the caller falls back to the 2D reveal otherwise. */
  static available(rarity: string, climb = true): boolean { return ChestStage.modelsFor(rarity, climb).every((n) => lib()?.hq(n) ?? false); }

  constructor(readonly rarity: string, width: number, height: number, readonly climb = true) {
    this.path = climb ? climbPath(ChestModel.rankOf(rarity)) : [ChestModel.rankOf(rarity)];
    this.view = new ChestCamera(width / height);
    this.bounds = ChestModel.bounds(rarity);
    this.gl = new StageCanvas(this.scene, this.camera, width, height, (real) => this.tick(real), () => this.disposeScene());
    stageLights(this.scene, this.gl.renderer);
    this.model = new ChestModel(this.scene, RARITIES[this.path[0]]);
    this.scene.add(groundShadow());
    this.rays = new StageRays(this.model.colour);
    this.scene.add(this.rays.mesh);
    this.finale = new StageFinale(this.scene, this.bounds.getCenter(new THREE.Vector3()));
    this.finale.onArc = () => this.onArc?.();
    this.framing = { x: width / 2, y: height / 2, fill: ChestStage.CardFill, w: width, h: height };
    this.applyFraming();
  }

  /** The share of the view's height the closed chest fills on the end-screen card, and full screen. */
  static readonly CardFill = 0.6;
  static readonly StageFill = 0.23;

  /** The rarity showing now (it climbs) and whether it has reached the real one. */
  get showing(): string { return this.model.rarity; }
  get atTop(): boolean { return this.step >= this.path.length - 1; }

  /** Frames the chest (chestCamera.ts) where `framing` says, and puts the rays behind it. */
  private applyFraming(): void {
    const f = this.framing;
    this.view.frame(this.bounds, f.w, f.h, f.y, f.fill, f.x);
    this.rays.place(this.view);
  }

  /** The entrance on the full-screen stage: the chest drops in from above and slams down. `onLand` at the impact. */
  enter(onLand: (() => void) | null = null): void {
    if (this.phase !== "shake" || reducedVfx()) { onLand?.(); return; }
    this.model.show.onLand = () => { this.dustUp(); onLand?.(); };
    this.model.show.drop();
    this.beckon = -1;
  }

  /**
   * The entrance from the end card: the chest lifts off where the card showed it (`from`, CSS px of this canvas, and
   * the share of the height it filled there) and flies, growing, to the stage's framing, where it lands with a slam.
   */
  flyIn(from: Framing, onLand: (() => void) | null = null): void {
    if (reducedVfx()) { onLand?.(); return; }
    this.flight = new ChestFlight(from, { x: this.framing.x, y: this.framing.y, fill: this.framing.fill }, onLand);
    Object.assign(this.framing, from);
    this.applyFraming();
    this.beckon = -1;
  }

  private dustUp(): void { this.vfx?.chestShake(this.chestPos(), this.model.rank); this.vfx?.chestShake(this.chestPos(), this.model.rank); }

  /**
   * The climb's next step (a tap while the chest is below its real rarity): the old chest goes in a flash and the next
   * tier's pops out of it, its colour on the sigil, the rays and the light. Returns the rarity it became, or null when
   * it already shows the real one.
   */
  upgrade(): string | null {
    if (this.phase !== "shake" || this.atTop) return null;
    this.step++;
    const prev = this.model;
    const next = new ChestModel(this.scene, RARITIES[this.path[this.step]]);
    next.show.adopt(prev.show);
    prev.dispose();
    this.model = next;
    this.rays.colour(next.colour);
    this.vfx?.ring(this.chestPos(), { size: 2.2, life: 0.5, color: next.colour });
    this.vfx?.sparkles(this.chestPos().add(_up), 18, { color: next.colour, radius: 0.7, rise: 1.6, life: 1.0, size: 0.16 });
    this.kick = 1;
    return next.rarity;
  }

  /** OPEN: the burst sequence, then `onOpened` when the clip has finished. */
  open(onOpened: () => void): void {
    if (this.phase !== "shake") return;
    this.phase = "open";
    this.onOpened = onOpened;
    this.openTime = 0;
    this.model.rig.playOnce("open", true, () => this.finishOpen());
  }

  /** Tap to skip: jump to the end of `open` (bursting on the way if it has not yet) and report opened. */
  skip(): void {
    while (this.phase === "shake" && !this.atTop) this.upgrade();
    if (this.phase === "shake") this.open(() => undefined);
    if (this.phase !== "open") return;
    this.model.rig.jumpToEnd();
    this.clock.reset();
    this.finishOpen();
  }

  private finishOpen(): void {
    if (this.phase !== "open") return;
    if (!this.burstDone) this.burst();
    this.phase = "done";
    this.fxClock = 0;
    if (this.model.rig.has("opened")) this.model.rig.playLoop("opened");
    const cb = this.onOpened; this.onOpened = null;
    cb?.();
  }

  /** Called at the burst (the lid thrown): the reveal flashes the screen and launches the cards from here. */
  onBurst: (() => void) | null = null;
  /** Called as the lock snaps (the slot plays it), and on each of the mythic's lightning arcs. */
  onLock: (() => void) | null = null;
  onArc: (() => void) | null = null;
  private lockDone = false;
  private burst(): void {
    this.burstDone = true;
    const rank = this.model.rank, plan = burstPlan(rank);
    this.rays.burst(plan.rays, plan.flashMs / 1000);
    this.view.push(plan.dolly);
    this.view.orbitFrom(this.clock.now);
    this.clock.slowMo();
    this.vfx?.chestBurst(this.chestPos(), rank, this.model.colour);
    this.model.show.burst();
    this.lidLight = 0.5;
    this.finale.burst(this.model);
    const cb = this.onBurst; this.onBurst = null;
    cb?.();
  }

  // ---- Full-screen stage ("chest slot machine") ------------------------------------------------------------------

  /** Charge 0..1 from the player's rattle taps: the rays, the sigil and the light inside ramp with it. */
  private chargeRays = 0.05;
  /** A decaying 0..1 punch per rattle tap: the chest jumps in scale. */
  private kick = 0;
  private lidLight = 0;

  /** One rattle tap during the charge (the `shake` clip, a scale punch, sparks); `rays` is revealPlan.chargeRays. */
  rattle(rays: number): void {
    if (this.phase !== "shake") return;
    this.kick = 1;
    this.chargeRays = rays;
    this.model.rig.playOnce("shake", false, null, { solo: true, timeScale: 1.15 });
    this.model.show.rattle(Math.min(1, (rays - 0.05) / 0.3));
    this.dustUp();
  }

  /**
   * Re-sizes the stage (the slot machine makes it full screen). `focusY` (CSS px from the top) is where the chest
   * sits and `fill` the share of the height it takes; by default the chest is centred at the card's fill.
   * `pixelRatio` is normally `ChestStage.pixelRatio()` (the game's effective tier).
   */
  resize(width: number, height: number, pixelRatio: number, focusY = height / 2, fill = ChestStage.CardFill): void {
    this.gl.size(width, height, pixelRatio);
    this.framing = { x: width / 2, y: focusY, fill, w: width, h: height };
    this.applyFraming();
  }

  /** The chest's mouth in CSS pixels relative to the canvas' top-left: where the reward cards fly out from. */
  mouthOnCanvas(): { x: number; y: number } {
    const p = this.model.show.mouthAt.clone().add(new THREE.Vector3(0, 0.1 * ChestStage.Scale, 0)).project(this.camera);
    return { x: ((p.x + 1) / 2) * this.gl.width, y: ((1 - p.y) / 2) * this.gl.height };
  }

  /** Where the effects anchor: the vfx add their own ~0.2-unit lift for the mouth, so meet the chest's mouth there. */
  private chestPos(): THREE.Vector3 { return this.model.show.mouthAt.clone().add(new THREE.Vector3(0, -0.2, 0)); }

  /** The radiant burst behind the chest. */
  private readonly rays: StageRays;
  private sway = 0;
  /** One frame (stageCanvas.ts runs the loop and renders after): `real` seconds, slowed by the burst's clock. */
  private tick(real: number): void {
    const dt = this.clock.tick(real);                               // the burst's slow motion
    const m = this.model, rank = m.rank;
    this.flightUpdate(real);
    m.rig.update(dt);
    this.fxClock += dt;
    this.sway += dt;
    HqMaterials.beamTime.value += dt;
    // the burst behind the chest: a faint breathing glow while it waits, a flash at the burst, then a sustained radiance
    this.rays.update(real, this.phase === "shake" ? this.rays.waiting(this.chargeRays) : !this.burstDone ? this.chargeRays : 0.16 + 0.025 * rank, this.camera);
    m.root.rotation.y = ChestModel.Yaw + Math.sin(this.sway * 0.7) * 0.09;   // a slow turntable sway so it never sits dead still
    if (this.kick > 0 && !m.show.dropping && !m.show.popping) {
      // rattle tap: a 12 % scale jump that decays over ~0.25 s, with a small sideways jolt
      this.kick = Math.max(0, this.kick - dt * 4);
      const k = this.kick * this.kick;
      m.root.scale.copy(m.show.baseScale).multiplyScalar(1 + 0.12 * k);
      m.root.rotation.z = Math.sin(this.sway * 60) * 0.06 * k;
    }
    if (this.phase === "shake") {
      if (this.fxClock >= ChestStage.ShakeEvery && this.chargeRays > 0.05) { this.fxClock = 0; this.vfx?.chestShake(this.chestPos(), rank); }
      // it rattles on its own now and then while it waits (not while it falls or flies, nor once the player has tapped)
      if (this.beckon >= 0 && this.chargeRays <= 0.05) {
        this.beckon += dt;
        if (this.beckon >= ChestStage.BeckonEvery) { this.beckon = 0; m.rig.playOnce("shake", false, null, { solo: true }); }
      } else if (!m.show.dropping && !this.flight && this.beckon < 0) this.beckon = 0;
    }
    if (this.burstDone) {
      // the lid's inside catches the light: a flare at the burst settling to a steady glow in the rank's colour
      this.lidLight += (0.14 - this.lidLight) * Math.min(1, dt * 2.5);
      m.lidGlow.value.setRGB(m.colour[0], m.colour[1], m.colour[2]).multiplyScalar(this.lidLight);
    }
    if (this.phase === "open") {
      this.openTime += dt;
      if (!this.lockDone && this.openTime >= ChestStage.LockAt) { this.lockDone = true; this.onLock?.(); }
      if (!this.burstDone && this.openTime >= m.burstAt) this.burst();
    }
    if (this.phase === "done" && this.fxClock >= ChestStage.AuraEvery) { this.fxClock = 0; this.vfx?.chestAura(this.chestPos(), rank, m.colour); }
    m.show.update(dt, m.lid ? hingeAngle(m.lid) : 0, this.camera);
    this.finale.update(dt, this.vfx, m);
    this.vfx?.update(Math.max(dt, real * StageClock.VfxFloor));
    // camera: the rank-scaled VFX shake plus the show's own trauma (the landing, the burst), the burst's push in, and
    // the slow turn round the open chest
    this.view.update(dt, this.clock.now, this.vfx?.shakeVec, m.show.kick);
  }

  /** When the lock snaps in the `open` clip (its frame 5 at 30 fps). */
  static readonly LockAt = 4 / 30;

  /** The flight from the end card (chestFlight.ts), in real time; it lands with the slam. */
  private flightUpdate(dt: number): void {
    const f = this.flight;
    if (!f) return;
    this.model.root.rotation.x = f.update(dt, this.framing.h, this.framing);
    this.applyFraming();
    if (!f.done) return;
    this.flight = null;
    this.model.show.land();
    this.dustUp();
    f.onLand?.();
  }

  /** Tears the stage down now (it also goes by itself once its canvas leaves the page). */
  dispose(): void { this.gl.dispose(); }

  private disposeScene(): void { this.model.dispose(); this.finale.dispose(); }
}

const _up = new THREE.Vector3(0, 0.3, 0);
